import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import {
  broadcastNotification,
  deleteNotification,
  getUserNotifications,
  readNotification,
  registerDevice,
  removeDevice,
  sendNotification,
} from "../../services/notificationService";
import {
  BroadcastNotificationRequest,
  BroadcastNotificationResponse,
  DeleteNotificationResponse,
  DeviceRegistrationResponse,
  DeviceRemovalResponse,
  FetchUserNotificationsRequest,
  GetUserNotificationsResponse,
  ReadNotificationResponse,
  SendNotificationRequest,
  SendNotificationResponse,
  UserNotification,
} from "../../types";
import {
  deleteWebFcmToken,
  getWebFcmToken,
} from "../../services/firebaseMessaging";
import {
  ERROR_MESSAGES,
  SUCCESS_MESSAGES,
  WEB_FCM_TOKEN_KEY,
  API_RESPONSE_STATUS,
} from "../../utils/constants";
import {
  clearDeviceRegistrationFailureCount,
  isDeviceRegistrationBlockedByFailures,
  recordDeviceRegistrationApiFailure,
} from "../../utils/deviceRegistrationLimiter";
import { ApiResponseStatus } from "../../types";

interface NotificationState {
  loading: boolean;
  success: boolean;
  error: string | null;
  message: string | null;
  notifications: UserNotification[];
  nextPageKey: string | null;
  total: number;
}

const initialState: NotificationState = {
  loading: false,
  success: false,
  error: null,
  message: null,
  notifications: [],
  nextPageKey: null,
  total: 0,
};

const createSuccessResponse = (
  message: string
): DeviceRegistrationResponse | DeviceRemovalResponse => ({
  status: API_RESPONSE_STATUS.SUCCESS as ApiResponseStatus,
  statusCode: 200,
  message,
  timestamp: new Date().toISOString(),
});

function notificationRowMatchesId(
  notification : UserNotification,
  notificationId: string
): boolean {
  return (
    notification.notificationId === notificationId ||
    notification.id === notificationId ||
    notification._id === notificationId
  );
}

function isTruthyReadFlag(value: unknown): boolean {
  return value === true || value === "true" || value === 1;
}

/** True if the row should count toward the header unread badge. */
function isNotificationUnread(notification: UserNotification): boolean {
  const read: unknown = notification.read;
  const isRead: unknown = notification.isRead;
  if (isTruthyReadFlag(read) || isTruthyReadFlag(isRead)) {
    return false;
  }
  const readStatus = (notification as { readStatus?: unknown }).readStatus;
  if (readStatus != null) {
    const readStatusString = String(readStatus).toUpperCase().trim();
    if (readStatusString === "READ") {
      return false;
    }
    if (readStatusString === "UNREAD") {
      return true;
    }
  }
  return !isTruthyReadFlag(read) && !isTruthyReadFlag(isRead);
}

// Register device for notifications
export const registerDeviceForNotifications = createAsyncThunk<
  DeviceRegistrationResponse,
  { userId: string; forceRefresh?: boolean },
  { rejectValue: { message: string } }
>(
  "notification/registerDevice",
  async ({ userId, forceRefresh = false }, { rejectWithValue }) => {
    if (isDeviceRegistrationBlockedByFailures()) {
      return createSuccessResponse(ERROR_MESSAGES.DEVICE_REGISTRATION_BLOCKED);
    }

    const fcmToken = await getWebFcmToken({ forceRefresh });
    if (!fcmToken) {
      return createSuccessResponse(ERROR_MESSAGES.DEVICE_TOKEN_UNAVAILABLE);
    }

    try {
      const response = await registerDevice({
        fcmToken,
        userId,
        platform: "WEB",
      });
      clearDeviceRegistrationFailureCount();
      return response;
    } catch (error: any) {
      recordDeviceRegistrationApiFailure();
      return rejectWithValue({
        message:
          error.response?.data?.message ||
          error.message ||
          ERROR_MESSAGES.DEVICE_REGISTRATION_FAILED,
      });
    }
  }
);

export const removeDeviceForNotifications = createAsyncThunk<
  DeviceRemovalResponse,
  void,
  { rejectValue: { message: string } }
>("notification/removeDevice", async (_, { rejectWithValue }) => {
  try {
    const fcmToken = localStorage.getItem(WEB_FCM_TOKEN_KEY);
    if (!fcmToken) {
      return createSuccessResponse(SUCCESS_MESSAGES.DEVICE_REMOVAL_SUCCESS);
    }

    const response = await removeDevice(fcmToken);
    await deleteWebFcmToken();
    localStorage.removeItem(WEB_FCM_TOKEN_KEY);
    clearDeviceRegistrationFailureCount();
    return response;
  } catch (error: any) {
    return rejectWithValue({
      message:
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.DEVICE_REMOVAL_FAILED,
    });
  }
});

export const sendNotificationToUser = createAsyncThunk<
  SendNotificationResponse,
  SendNotificationRequest,
  { rejectValue: { message: string } }
>("notification/send", async (data, { rejectWithValue }) => {
  try {
    const response = await sendNotification(data);
    return response;
  } catch (error: any) {
    return rejectWithValue({
      message:
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.NOTIFICATION_SEND_FAILED,
    });
  }
});

export const broadcastNotificationToUsers = createAsyncThunk<
  BroadcastNotificationResponse,
  BroadcastNotificationRequest,
  { rejectValue: { message: string } }
>("notification/broadcast", async (data, { rejectWithValue }) => {
  try {
    const response = await broadcastNotification(data);
    return response;
  } catch (error: any) {
    return rejectWithValue({
      message:
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.BROADCAST_NOTIFICATION_FAILED,
    });
  }
});

export const fetchNotificationsForUser = createAsyncThunk<
  GetUserNotificationsResponse,
  FetchUserNotificationsRequest,
  { rejectValue: { message: string } }
>("notification/fetchByUser", async ({ userId, nextPageKey = null, limit = 10 }, { rejectWithValue }) => {
  try {
    const response = await getUserNotifications(userId, nextPageKey, limit);
    return response;
  } catch (error: any) {
    return rejectWithValue({
      message:
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.FETCH_USER_NOTIFICATIONS_FAILED,
    });
  }
});

export const readNotificationById = createAsyncThunk<
  ReadNotificationResponse,
  { notificationId: string; userId?: string },
  { rejectValue: { message: string }; state: RootState }
>("notification/readById", async ({ notificationId, userId }, { rejectWithValue, getState }) => {
  try {
    const effectiveUserId = userId || getState().auth.userId;
    if (!effectiveUserId) {
      return rejectWithValue({
        message: ERROR_MESSAGES.NOT_LOGGED_IN,
      });
    }
    const response = await readNotification(notificationId, effectiveUserId);
    return response;
  } catch (error: any) {
    return rejectWithValue({
      message:
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.READ_NOTIFICATION_FAILED,
    });
  }
});

export const deleteNotificationById = createAsyncThunk<
  DeleteNotificationResponse,
  { notificationId: string; userId?: string },
  { rejectValue: { message: string }; state: RootState }
>("notification/deleteById", async ({ notificationId, userId }, { rejectWithValue, getState }) => {
  try {
    const effectiveUserId = userId || getState().auth.userId;
    if (!effectiveUserId) {
      return rejectWithValue({
        message: ERROR_MESSAGES.NOT_LOGGED_IN,
      });
    }
    const response = await deleteNotification(notificationId, effectiveUserId);
    return response;
  } catch (error: any) {
    return rejectWithValue({
      message:
        error.response?.data?.message ||
        error.message ||
        ERROR_MESSAGES.DELETE_NOTIFICATION_FAILED,
    });
  }
});

const notificationSlice = createSlice({
  name: "notification",
  initialState,
  reducers: {
    clearNotificationRegistrationState: (state) => {
      state.loading = false;
      state.success = false;
      state.error = null;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerDeviceForNotifications.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(registerDeviceForNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.message = action.payload?.message || SUCCESS_MESSAGES.DEVICE_REGISTRATION_SUCCESS;
      })
      .addCase(registerDeviceForNotifications.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error =
          action.payload?.message || ERROR_MESSAGES.DEVICE_REGISTRATION_FAILED;
      })
      .addCase(removeDeviceForNotifications.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(removeDeviceForNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.message =
          action.payload?.message || SUCCESS_MESSAGES.DEVICE_REMOVAL_SUCCESS;
      })
      .addCase(removeDeviceForNotifications.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error =
          action.payload?.message || ERROR_MESSAGES.DEVICE_REMOVAL_FAILED;
      })
      .addCase(sendNotificationToUser.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(sendNotificationToUser.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.message =
          action.payload?.message || SUCCESS_MESSAGES.NOTIFICATION_SENT_SUCCESS;
      })
      .addCase(sendNotificationToUser.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error =
          action.payload?.message || ERROR_MESSAGES.NOTIFICATION_SEND_FAILED;
      })
      .addCase(broadcastNotificationToUsers.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(broadcastNotificationToUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.message =
          action.payload?.message ||
          SUCCESS_MESSAGES.BROADCAST_NOTIFICATION_SUCCESS;
      })
      .addCase(broadcastNotificationToUsers.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error =
          action.payload?.message ||
          ERROR_MESSAGES.BROADCAST_NOTIFICATION_FAILED;
      })
      .addCase(fetchNotificationsForUser.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.loading = true;
          state.success = false;
          state.error = null;
        }
      })
      .addCase(fetchNotificationsForUser.fulfilled, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.loading = false;
          state.success = true;
          state.error = null;
        }
        const payload = action.payload as
          | GetUserNotificationsResponse
          | UserNotification[]
          | undefined;
        let nextNotifications: UserNotification[] = [];
        let responseNextPageKey: string | null = null;
        let responseTotal = 0;
        if (Array.isArray(payload)) {
          nextNotifications = payload;
          responseTotal = payload.length;
        } else if (Array.isArray(payload?.data)) {
          nextNotifications = payload?.data as UserNotification[];
          responseTotal = nextNotifications.length;
        } else if (Array.isArray(payload?.data?.notifications)) {
          nextNotifications = payload?.data?.notifications ?? [];
          responseNextPageKey = payload?.data?.nextPageKey ?? null;
          responseTotal = payload?.data?.total ?? nextNotifications.length;
        }

        if (action.meta.arg?.silent) {
          if (
            JSON.stringify(state.notifications) ===
            JSON.stringify(nextNotifications)
          ) {
            return;
          }
        }

        state.message = action.payload?.message || null;
        state.nextPageKey = responseNextPageKey;
        const isPaginatedFetch = !!action.meta.arg?.nextPageKey;
        if (isPaginatedFetch) {
          const existingIds = new Set(
            state.notifications.map(
              (n) => n.notificationId || n.id || n._id || `${n.createdAt}-${n.title}`
            )
          );
          const merged = [...state.notifications];
          nextNotifications.forEach((notification) => {
            const notificationKey =
              notification.notificationId ||
              notification.id ||
              notification._id ||
              `${notification.createdAt}-${notification.title}`;
            if (!existingIds.has(notificationKey)) {
              merged.push(notification);
            }
          });
          state.notifications = merged;
          state.total = Math.max(state.total, responseTotal, state.notifications.length);
        } else {
          state.notifications = nextNotifications;
          state.total = Math.max(responseTotal, state.notifications.length);
        }
      })
      .addCase(fetchNotificationsForUser.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error =
          action.payload?.message ||
          ERROR_MESSAGES.FETCH_USER_NOTIFICATIONS_FAILED;
      })
      .addCase(readNotificationById.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(readNotificationById.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.message = action.payload?.message || null;
        const { notificationId } = action.meta.arg;
        const row = state.notifications.find((n) =>
          notificationRowMatchesId(n, notificationId)
        );
        if (row) {
          row.read = true;
          row.isRead = true;
        }
      })
      .addCase(readNotificationById.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error =
          action.payload?.message || ERROR_MESSAGES.READ_NOTIFICATION_FAILED;
      })
      .addCase(deleteNotificationById.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(deleteNotificationById.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.error = null;
        state.message = action.payload?.message || null;
        const { notificationId } = action.meta.arg;
        state.notifications = state.notifications.filter(
          (notification) => !notificationRowMatchesId(notification, notificationId)
        );
      })
      .addCase(deleteNotificationById.rejected, (state, action) => {
        state.loading = false;
        state.success = false;
        state.error =
          action.payload?.message || ERROR_MESSAGES.DELETE_NOTIFICATION_FAILED;
      });
  },
});

export const { clearNotificationRegistrationState } = notificationSlice.actions;
export default notificationSlice.reducer;

export const notificationSelector = (state: RootState) => state.notification;

/** Count notifications not marked read (supports common API shapes). */
export const selectUnreadNotificationCount = (state: RootState): number =>
  state.notification.notifications.filter(isNotificationUnread).length;
